<template>
  <section id="organization">
    <div class="page">
      <!-- Détails de l'organisation : modifiables par un owner, en lecture seule pour un member -->
      <form v-if="isOwner" class="card" @submit.prevent="saveOrganizationDetails">
        <organization-details-fields v-model="details" />

        <div class="grid-x">
          <div class="cell auto" />
          <div class="cell shrink">
            <app-button type="submit" size="large" :loading="isSavingDetails">
              Save organization details
            </app-button>
          </div>
        </div>
      </form>
      <div v-else-if="organization" class="card organization-details">
        <div class="grid-x row grid-margin-x">
          <div class="cell auto">
            <app-label>Organization name</app-label>
            <p>{{ organization.name }}</p>
          </div>
          <div class="cell auto" v-if="plan">
            <app-label>Plan</app-label>
            <p>{{ PackageTypesLabel[plan.type] || plan.type }}</p>
          </div>
        </div>
        <div class="grid-x row grid-margin-x" v-if="addressLines.length">
          <div class="cell auto">
            <app-label>Address</app-label>
            <p v-for="(line, index) in addressLines" :key="index">{{ line }}</p>
          </div>
        </div>
      </div>

      <!-- Liste des membres -->
      <div class="card">
        <div class="grid-x grid-margin-x header-row">
          <div class="cell auto">
            <app-label>Members</app-label>
          </div>
          <div class="cell shrink" v-if="isOwner">
            <app-button size="small" @click="showAddMemberModal = true">
              Add existing member
            </app-button>
          </div>
        </div>

        <div class="members-list" v-if="members && members.length">
          <div class="member-row grid-x grid-margin-x" v-for="member in members" :key="member.organizationMemberId">
            <div class="cell auto member-info">
              <p class="member-id">{{ member.email || member.userId }}</p>
              <span class="member-role" :class="{ 'owner': member.role === 'OWNER' }">{{ member.role }}</span>
            </div>
            <div class="cell shrink member-actions" v-if="isOwner && member.userId !== currentUserId && !member.isCreator">
              <app-button size="small" look="secondarySquare" @click="confirmChangeRole(member)">
                {{ member.role === 'OWNER' ? 'Set Member' : 'Set Owner' }}
              </app-button>
              <app-button size="small" look="warning" @click="confirmRemoveMember(member)">Remove</app-button>
            </div>
          </div>
        </div>
        <p class="empty-state" v-else-if="!isLoading">No members found.</p>

        <!-- Quitter l'organisation : jamais son organisation personnelle -->
        <div class="leave-section" v-if="organization && !isPersonalOrganization">
          <app-button look="warning" @click="confirmLeave">Leave organization</app-button>
        </div>
      </div>

      <!-- Ajout d'un membre existant -->
      <app-modal
        :show.sync="showAddMemberModal"
        title="Add an existing member"
        class="add-member-modal"
      >
        <form @submit.prevent="addExistingMember">
          <app-label>Email address</app-label>
          <app-input
            v-model="memberEmail"
            type="email"
            required
            placeholder="colleague@company.com"
          />
          <p class="modal-help">
            Enter the email address of an active Doppio user. They will be added immediately
            to this organization as a member. No invitation email will be sent.
          </p>
          <div class="modal-actions">
            <app-button type="submit" :loading="isAddingMember">Add member</app-button>
          </div>
        </form>
      </app-modal>
    </div>
  </section>
</template>

<script>
import organizationApi from '@/services/api/organization';
import countriesData from '@/services/data/countries';
import PackageTypesLabel from '@/enums/packageTypesLabel';
import OrganizationDetailsFields, { emptyAddress } from '@/components/OrganizationDetailsFields.vue';

export default {
  name: 'organization',

  components: {
    OrganizationDetailsFields,
  },

  props: {
    me: {
      type: Object,
      default: null,
    },
    isOwner: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      organization: null,
      members: [],
      plan: null,
      details: { name: '', address: emptyAddress() },
      PackageTypesLabel,
      isLoading: true,
      isSavingDetails: false,
      showAddMemberModal: false,
      memberEmail: '',
      isAddingMember: false,
    };
  },

  computed: {
    currentUserId() {
      return this.me ? this.me.userId : null;
    },
    isPersonalOrganization() {
      return !!this.me && !!this.organization
        && this.me.personalOrganizationId === this.organization.organizationId;
    },
    addressLines() {
      const address = this.organization && this.organization.address;
      if (!address) return [];
      return [
        address.line1,
        address.line2,
        [address.postalCode, address.city].filter(Boolean).join(' '),
        countriesData[address.country] || address.country,
      ].filter(Boolean);
    },
  },

  async mounted() {
    await this.loadOrganization();
  },

  methods: {
    async loadOrganization() {
      this.isLoading = true;
      try {
        const data = await organizationApi.getOrganization();
        this.organization = data.organization;
        this.members = data.members;
        this.plan = data.package;
        this.details = {
          name: this.organization.name,
          address: this.organization.address || emptyAddress(),
        };
      } catch (error) {
        this.$message.show({
          title: 'Error',
          text: 'Unable to load organization data',
          confirmText: 'Ok',
          hasCancel: false,
        });
      } finally {
        this.isLoading = false;
      }
    },

    async saveOrganizationDetails() {
      this.isSavingDetails = true;
      try {
        const organization = await organizationApi.editOrganization({
          name: this.details.name,
          address: { ...this.details.address, state: this.details.address.state || 'unavailable' },
        });

        this.organization = organization;
        this.details = {
          name: organization.name,
          address: organization.address || emptyAddress(),
        };
        this.$notification.show({ text: 'Organization details updated!' });
        this.$emit('organizationUpdated');
      } catch (error) {
        this.$message.show({
          title: 'Error',
          text: 'Unable to update the organization details',
          confirmText: 'Ok',
          hasCancel: false,
        });
      } finally {
        this.isSavingDetails = false;
      }
    },

    async addExistingMember() {
      this.isAddingMember = true;
      const email = this.memberEmail.trim();
      try {
        await organizationApi.addExistingMember(email);
        this.$notification.show({ text: `${email} was added as a member of this organization.` });
        this.memberEmail = '';
        this.showAddMemberModal = false;
        await this.loadOrganization();
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Unable to add member';
        this.$message.show({
          title: 'Error',
          text: msg,
          confirmText: 'Ok',
          hasCancel: false,
        });
      } finally {
        this.isAddingMember = false;
      }
    },

    confirmRemoveMember(member) {
      this.$message.show({
        title: 'Remove member',
        text: 'Are you sure you want to remove this member from your organization?',
        confirmText: 'Remove',
        confirmLook: 'warning',
        cancelText: 'Cancel',
        onConfirm: () => this.removeMember(member),
        hasCancel: true,
      });
    },

    async removeMember(member) {
      try {
        await organizationApi.removeMember(member.userId);
        this.$notification.show({ text: 'Member removed from organization' });
        await this.loadOrganization();
      } catch (error) {
        this.$message.show({
          title: 'Error',
          text: this.getErrorMessage(error, 'Unable to remove member'),
          confirmText: 'Ok',
          hasCancel: false,
        });
      }
    },

    confirmChangeRole(member) {
      const newRole = member.role === 'OWNER' ? 'MEMBER' : 'OWNER';
      const memberName = member.email || member.userId;
      const isPromotion = newRole === 'OWNER';

      this.$message.show({
        title: isPromotion ? 'Set owner' : 'Set member',
        text: isPromotion
          ? `${memberName} will be able to manage members, billing, and organization settings.`
          : `${memberName} will lose owner permissions for this organization.`,
        confirmText: isPromotion ? 'Set owner' : 'Set member',
        confirmLook: isPromotion ? 'primarySquare' : 'warning',
        cancelText: 'Cancel',
        onConfirm: () => this.toggleRole(member),
        hasCancel: true,
      });
    },

    async toggleRole(member) {
      const newRole = member.role === 'OWNER' ? 'MEMBER' : 'OWNER';
      try {
        await organizationApi.changeRole(member.userId, newRole);
        this.$notification.show({ text: `Role updated to ${newRole}` });
        await this.loadOrganization();
      } catch (error) {
        this.$message.show({
          title: 'Error',
          text: this.getErrorMessage(error, 'Unable to change member role'),
          confirmText: 'Ok',
          hasCancel: false,
        });
      }
    },

    confirmLeave() {
      this.$message.show({
        title: 'Leave organization',
        text: 'Are you sure you want to leave this organization?',
        confirmText: 'Leave',
        confirmLook: 'warning',
        cancelText: 'Cancel',
        onConfirm: () => this.leaveOrganization(),
        hasCancel: true,
      });
    },

    async leaveOrganization() {
      try {
        await organizationApi.leaveOrganization();
        this.$notification.show({ text: 'You have left the organization' });
        this.$emit('organizationContextChanged');
        this.$router.push({ name: 'home' });
      } catch (error) {
        this.$message.show({
          title: 'Error',
          text: this.getErrorMessage(error, 'Unable to leave organization'),
          confirmText: 'Ok',
          hasCancel: false,
        });
      }
    },

    getErrorMessage(error, fallback) {
      return error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : fallback;
    },
  },
};
</script>

<style lang="sass">
#organization
  .page
    @include page

    .card
      background-color: $primary90
      border-radius: 8px
      padding: 40px
      margin-bottom: 40px

    .header-row
      align-items: center
      margin-bottom: 20px

    .organization-details
      p
        margin: 4px 0 0
        color: $white
        font-size: 14px

    .row
      margin-bottom: 20px

    .members-list
      .member-row
        align-items: center
        padding: 12px 0
        border-bottom: 1px solid $primary70

        &:last-child
          border-bottom: none

        .member-info
          display: flex
          align-items: center
          gap: 12px

          .member-id
            margin: 0
            color: $white
            font-size: 14px

          .member-role
            display: inline-block
            padding: 4px 10px
            border-radius: 12px
            font-size: 11px
            line-height: 1
            background: rgba(255, 255, 255, 0.15)
            color: $primary20

            &.owner
              background: $accent100
              color: $white

        .member-actions
          display: flex
          gap: 8px

    .leave-section
      margin-top: 24px
      padding-top: 20px
      border-top: 1px solid $primary70

    .empty-state
      color: $primary30
      font-size: 14px

    .modal-help
      color: $primary30
      font-size: $font-3xs
      line-height: 1.5
      margin: 12px 0 0

    .add-member-modal
      .modal-wrapper
        background-color: $primary90

        h2
          color: $white

        .modal-content
          p
            color: $primary30

    .modal-actions
      margin-top: 20px
      display: flex
      justify-content: flex-end
</style>
